import type { CmmdForgeTaskDetail, CmmdMember } from "@cmmd-forge/contracts";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";

export interface ProjectTaskAdditionalDetailsProps {
  readonly detail: CmmdForgeTaskDetail | null;
  readonly members: readonly CmmdMember[];
}

export function ProjectTaskAdditionalDetails(props: ProjectTaskAdditionalDetailsProps) {
  if (!props.detail) return null;
  const detail = props.detail;
  const memberById = new Map(props.members.map((member) => [Number(member.id), member.name]));
  const collaborators = detail.collaborators?.map((id) => memberById.get(id) ?? `User #${id}`);
  const sources = [
    detail.feedbackId ? `Feedback #${detail.feedbackId}` : null,
    detail.specDocumentId ? `Spec #${detail.specDocumentId}` : null,
  ].filter((value): value is string => value !== null);
  const rows = [
    { label: "Labels", value: joinOrNull(detail.tags) },
    { label: "Collaborators", value: joinOrNull(collaborators) },
    { label: "Acceptance", value: humanize(detail.acceptanceStatus) },
    { label: "Estimate", value: formatDuration(detail.estimatedMinutes) },
    { label: "Time spent", value: formatDuration(detail.actualMinutes) },
    { label: "Recurrence", value: recurrenceLabel(detail) },
    { label: "Repository", value: detail.githubRepo },
    {
      label: "Created by",
      value:
        detail.createdBy === null
          ? null
          : (memberById.get(detail.createdBy) ?? `User #${detail.createdBy}`),
    },
    {
      label: "Confidence",
      value: detail.confidenceScore === null ? null : `${detail.confidenceScore}%`,
    },
    { label: "Top task", value: detail.isTopTaskToday ? "Today" : null },
    { label: "Related to", value: relatedLabel(detail) },
    {
      label: "Sources",
      value: sources.length > 0 ? sources.join(" · ") : null,
    },
  ].filter((row): row is { readonly label: string; readonly value: string } => row.value !== null);

  if (rows.length === 0) return null;

  return (
    <details className="mt-4 border-t border-border pt-3">
      <summary
        className={`${RESPONSIVE_TEXT_BUTTON_CLASS} flex cursor-pointer items-center text-xs font-medium uppercase tracking-wide text-muted-foreground`}
      >
        Planning ({rows.length})
      </summary>
      <dl className="mt-1.5 space-y-1 text-sm">
        {rows.map((row) => (
          <Detail key={row.label} label={row.label} value={row.value} />
        ))}
      </dl>
    </details>
  );
}

function Detail(props: { readonly label: string; readonly value: string | null }) {
  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-start gap-2 py-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{props.label}</dt>
      <dd className="min-w-0 break-words text-foreground">{props.value ?? "None"}</dd>
    </div>
  );
}

function joinOrNull(values: readonly string[] | null | undefined): string | null {
  return values && values.length > 0 ? values.join(", ") : null;
}

function humanize(value: string | null): string | null {
  if (!value) return null;
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDuration(minutes: number | null): string | null {
  if (minutes === null) return null;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

function recurrenceLabel(detail: CmmdForgeTaskDetail): string | null {
  if (!detail.recurrence) return null;
  const end = detail.recurrenceEnd ? ` until ${formatDate(detail.recurrenceEnd)}` : "";
  return `${humanize(detail.recurrence) ?? detail.recurrence}${end}`;
}

function relatedLabel(detail: CmmdForgeTaskDetail): string | null {
  if (!detail.relatedToType || detail.relatedToId === null) return null;
  return `${humanize(detail.relatedToType)} #${detail.relatedToId}`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}
